import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
    AppBlockPort,
} from '@appblocks/codegeneration/types';
import * as tios from './tios';
import * as simulator from './simulator';

function getFieldDataType(dataType: string): TibboDataType {
    switch (dataType) {
        case 'S':
            return TibboDataType.STRING;
        case 'F':
            return TibboDataType.FLOAT;
        case 'M':
            return TibboDataType.TIME;
        case 'E':
            return TibboDataType.DATETIME;
        default:
            return TibboDataType.STRING;
    }
}

export default class TableNode extends BaseNode {
    constructor() {
        super();
        this.name = 'Table Lookup';
        this.id = 'node_table';
        this.icon = 'HiOutlineDatabase';
        this.requirements = ['tios'];
        this.linkedFeature = 'general';

        this.inputs = [
            {
                name: 'Find Record',
                portId: 'tbl_record_find',
                params: [
                    {
                        name: 'field_value',
                        dataType: TibboDataType.STRING,
                        default: '',
                    },
                    {
                        name: 'find_index',
                        dataType: TibboDataType.BYTE,
                        default: '0',
                        description: 'The Nth record that matches the search criteria, starting from 0',
                    },
                ],
            },
        ];

        const settings = [
            {
                id: 'TABLE',
                name: 'Table',
                settingType: SettingType.OPTION,
                refId: 'tbl_table',
                required: true,
                default: '',
                options: [] as any[],
            },
            {
                id: 'WRAPAROUND',
                name: 'Wrap Around',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'FIELD_NAME',
                name: 'Field Name',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
                options: [] as any[],
            },
            {
                id: 'OPERATOR',
                name: 'Operator',
                settingType: SettingType.OPTION,
                required: true,
                default: '=',
                options: [
                    {
                        name: 'Equals',
                        value: 'PL_FD_FIND_EQUAL',
                    },
                    {
                        name: 'Not Equal',
                        value: 'PL_FD_FIND_NOT_EQUAL',
                    },
                    {
                        name: 'Greater Than',
                        value: 'PL_FD_FIND_GREATER',
                    },
                    {
                        name: 'Greater Than Equal To',
                        value: 'PL_FD_FIND_GREATER_EQUAL',
                    },
                    {
                        name: 'Less Than',
                        value: 'PL_FD_FIND_LESSER',
                    },
                    {
                        name: 'Less Than Equal To',
                        value: 'PL_FD_FIND_LESSER_EQUAL',
                    },
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const project: ProjectConfiguration = this.project;
        const TABLE = project.getFeature(this.getSettingValue('TABLE'));
        if (TABLE === undefined) {
            return super.getSettingOptions(settingId);
        }
        const FIELDS = TABLE.getSettingValue('FIELDS');
        if (settingId === 'FIELD_NAME') {
            return FIELDS.map((field: BaseFeature) => ({
                name: `${field.getSettingValue('NAME')} (${field.getSettingValue('DISPLAY_NAME')})`,
                value: field.getSettingValue('NAME'),
            }));
        }
        return super.getSettingOptions(settingId);
    }

    getOutputs(): AppBlockPort[] {
        const project: ProjectConfiguration = this.project;
        const TABLE = project.getFeature(this.getSettingValue('TABLE'));
        if (TABLE === undefined) {
            return [];
        }
        const FIELDS = TABLE.getSettingValue('FIELDS');
        const fields = [
            {
                name: 'row_index',
                dataType: TibboDataType.WORD,
            },
        ];
        for (let i = 0; i < FIELDS.length; i += 1) {
            const field = FIELDS[i];
            fields.push({
                name: field.getSettingValue('NAME'),
                dataType: getFieldDataType(field.getSettingValue('FIELDTYPE')),
            });
        }
        this.outputs = [
            {
                name: 'Record Found',
                portId: 'tbl_record_found',
                params: fields,
            },
            {
                name: 'Record Not Found',
                portId: 'tbl_record_not_found',
                params: [],
            },
        ];
        return this.outputs;
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project: ProjectConfiguration = this.project;
            const TABLE = project.getFeature(this.getSettingValue('TABLE'));
            const OPERATOR = this.getSettingValue('OPERATOR');
            if (TABLE) {
                const links = project.getNodeLinks(this, 'tbl_record_find');
                let value = '';
                let operatorName = '';
                if (links.length > 0) {
                    const link = links[0];
                    value = project.getNodeParameterValue(this, 'field_value');
                }
                switch (OPERATOR) {
                    case 'PL_FD_FIND_EQUAL':
                        operatorName = '==';
                        break;
                    case 'PL_FD_FIND_NOT_EQUAL':
                        operatorName = '!=';
                        break;
                    case 'PL_FD_FIND_GREATER':
                        operatorName = '>';
                        break;
                    case 'PL_FD_FIND_GREATER_EQUAL':
                        operatorName = '>=';
                        break;
                    case 'PL_FD_FIND_LESSER':
                        operatorName = '<';
                        break;
                    case 'PL_FD_FIND_LESSER_EQUAL':
                        operatorName = '<=';
                        break;
                    default:
                        operatorName = '=';
                        break;
                }
                return `Table {{$${TABLE.getSettingValue('NAME')}}} lookup WHERE \n${this.getSettingValue('FIELD_NAME')} ${operatorName} ${value}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const TABLE = project.getFeature(this.getSettingValue('TABLE'));
        const OPERATOR = this.getSettingValue('OPERATOR');
        if (TABLE) {
            const links = project.getNodeLinks(this, 'tbl_record_find');
            let value = '';
            let operatorName = '';
            if (links.length > 0) {
                const link = links[0];
                value = project.getNodeParameterValue(this, 'field_value');
            }
            switch (OPERATOR) {
                case 'PL_FD_FIND_EQUAL':
                    operatorName = '==';
                    break;
                case 'PL_FD_FIND_NOT_EQUAL':
                    operatorName = '!=';
                    break;
                case 'PL_FD_FIND_GREATER':
                    operatorName = '>';
                    break;
                case 'PL_FD_FIND_GREATER_EQUAL':
                    operatorName = '>=';
                    break;
                case 'PL_FD_FIND_LESSER':
                    operatorName = '<';
                    break;
                case 'PL_FD_FIND_LESSER_EQUAL':
                    operatorName = '<=';
                    break;
                default:
                    operatorName = '=';
                    break;
            }
            return `Table {{$${TABLE.getSettingValue('NAME')}}} lookup WHERE \n${this.getSettingValue('FIELD_NAME')} ${operatorName} ${value}`;
        }
        return super.getName();
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'simulator':
                return simulator.getCodeBlocks(this);
            case 'tios':
                return tios.getCodeBlocks(this);
            default:
                return [];
        }
    }
}
